import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MergeType as MergeIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ManageSearch as ScanIcon,
  FormatListBulleted as ViewPagesIcon,
} from '@mui/icons-material';
import { tagManagementAPI } from '../services/api';
import { authAPI } from '../services/api';
import { colors } from '../theme';

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  {
    key: 'service_types',
    label: '服務類型 Service Types',
    getAll: (p) => tagManagementAPI.getServiceTypes(p),
    create: (d) => tagManagementAPI.createServiceType(d),
    update: (id, d) => tagManagementAPI.updateServiceType(id, d),
    remove: (id) => tagManagementAPI.deleteServiceType(id),
    merge: (cid, ids) => tagManagementAPI.mergeServiceTypes(cid, ids),
    scan: (id) => tagManagementAPI.scanServiceType(id),
    getScanStatus: (id) => tagManagementAPI.getServiceTypeScanStatus(id),
    scanLabel: 'Scan all pages for this service type',
    queryKey: 'service_types',
  },
  {
    key: 'keywords',
    label: '關鍵字 Keywords',
    getAll: (p) => tagManagementAPI.getKeywords(p),
    create: (d) => tagManagementAPI.createKeyword(d),
    update: (id, d) => tagManagementAPI.updateKeyword(id, d),
    remove: (id) => tagManagementAPI.deleteKeyword(id),
    merge: (cid, ids) => tagManagementAPI.mergeKeywords(cid, ids),
    scan: (id) => tagManagementAPI.scanKeyword(id),
    getScanStatus: (id) => tagManagementAPI.getKeywordScanStatus(id),
    scanLabel: 'Scan all pages for this keyword',
    queryKey: 'keywords',
  },
  {
    key: 'beneficiaries',
    label: '受惠者 Beneficiaries',
    getAll: (p) => tagManagementAPI.getBeneficiaries(p),
    create: (d) => tagManagementAPI.createBeneficiary(d),
    update: (id, d) => tagManagementAPI.updateBeneficiary(id, d),
    remove: (id) => tagManagementAPI.deleteBeneficiary(id),
    merge: (cid, ids) => tagManagementAPI.mergeBeneficiaries(cid, ids),
    scan: (id) => tagManagementAPI.scanBeneficiary(id),
    getScanStatus: (id) => tagManagementAPI.getBeneficiaryScanStatus(id),
    scanLabel: 'Scan all pages for this beneficiary',
    queryKey: 'beneficiaries',
  },
  {
    key: 'co_organizers',
    label: '合作單位 Co-organizers',
    getAll: (p) => tagManagementAPI.getCoOrganizers(p),
    create: (d) => tagManagementAPI.createCoOrganizer(d),
    update: (id, d) => tagManagementAPI.updateCoOrganizer(id, d),
    remove: (id) => tagManagementAPI.deleteCoOrganizer(id),
    merge: (cid, ids) => tagManagementAPI.mergeCoOrganizers(cid, ids),
    scan: (id) => tagManagementAPI.scanCoOrganizer(id),
    getScanStatus: (id) => tagManagementAPI.getCoOrganizerScanStatus(id),
    scanLabel: 'Scan all pages for this co-organizer',
    queryKey: 'co_organizers',
  },
];

// ─── Inline edit row ──────────────────────────────────────────────────────────
function EditableRow({ row, onSave, onCancel }) {
  const [name, setName] = useState(row.name);
  return (
    <TableRow sx={{ background: '#fffde7' }}>
      <TableCell padding="checkbox" />
      <TableCell>
        <TextField
          value={name}
          size="small"
          onChange={(e) => setName(e.target.value)}
          autoFocus
          fullWidth
        />
      </TableCell>
      <TableCell align="center">{row.page_count}</TableCell>
      <TableCell align="right">
        <IconButton size="small" color="primary" onClick={() => onSave(row.id, name)}>
          <SaveIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onCancel}>
          <CancelIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

// ─── Tag table panel ──────────────────────────────────────────────────────────
function TagPanel({ tab, authReady }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Keyword-scan state: Set of keyword IDs currently being scanned
  const [scanningIds, setScanningIds] = useState(new Set());
  const pollIntervalRef = useRef(null);

  const supportsScan = Boolean(tab.scan && tab.getScanStatus);

  const startPolling = useCallback((tagId) => {
    setScanningIds((prev) => new Set([...prev, tagId]));
  }, []);

  const stopPolling = useCallback((tagId) => {
    setScanningIds((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
  }, []);

  // Poll scan_status for all scanning tags every 3s
  useEffect(() => {
    if (!supportsScan || scanningIds.size === 0) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    if (pollIntervalRef.current) return; // already polling

    pollIntervalRef.current = setInterval(async () => {
      const ids = [...scanningIds];
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await tab.getScanStatus(id);
            if (!res.data.scanning) {
              stopPolling(id);
              queryClient.invalidateQueries(tab.queryKey);
              const pageCount = res.data?.page_count;
              if (pageCount !== undefined) {
                setSuccess(`Scan complete: ${pageCount} page(s) linked`);
              }
            }
          } catch {
            stopPolling(id); // stop on error too
          }
        })
      );
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [supportsScan, scanningIds, stopPolling, queryClient, tab, setSuccess]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries(tab.queryKey),
    [queryClient, tab.queryKey]
  );

  const triggerScan = useCallback(async (tagId) => {
    try {
      const res = await tab.scan(tagId);
      if (res.data?.scanning) {
        startPolling(tagId);
        invalidate();
        const matched = res.data?.matched;
        if (matched !== undefined && matched > 0) {
          setSuccess(
            `Linked ${matched} page(s) by text match; semantic scan continuing in background…`
          );
        }
      } else {
        invalidate();
        const matched = res.data?.matched;
        const totalLinked = res.data?.total_linked;
        if (matched !== undefined) {
          const msg = totalLinked !== undefined
            ? `Scan complete: ${totalLinked} page(s) linked${matched ? ` (+${matched} new)` : ''}`
            : matched > 0
              ? `Scan complete: matched ${matched} page(s)`
              : 'Scan complete: no new pages matched';
          setSuccess(msg);
        }
      }
    } catch (e) {
      setError('Failed to start page scan');
    }
  }, [startPolling, tab, invalidate]);

  // Merge dialog
  const [mergeOpen, setMergeOpen] = useState(false);
  const [canonicalId, setCanonicalId] = useState('');

  // Delete confirm dialog
  const [deleteId, setDeleteId] = useState(null);

  const { data: responseData, isLoading, isError, error: queryError, refetch } = useQuery(
    [tab.queryKey, search, page, pageSize],
    () => tab.getAll({ ...(search ? { search } : {}), page, page_size: pageSize }),
    { keepPreviousData: true, enabled: authReady, retry: 1 }
  );

  const rows = responseData?.data?.results ?? responseData?.data ?? [];
  const totalCount = responseData?.data?.count ?? rows.length;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const createMutation = useMutation((name) => tab.create({ name, is_active: true }), {
    onSuccess: (res) => {
      invalidate();
      setNewName('');
      setAddingNew(false);
      setSuccess('Created');
      // Auto-scan pages when a new scannable tag is added
      if (supportsScan && res?.data?.id) {
        triggerScan(res.data.id);
      }
    },
    onError: (e) => {
      const data = e.response?.data;
      setError(
        data?.name?.[0] || data?.name_en?.[0] || data?.name_zh?.[0] || 'Create failed'
      );
    },
  });

  const updateMutation = useMutation(({ id, name }) => tab.update(id, { name }), {
    onSuccess: () => { invalidate(); setEditingId(null); setSuccess('Saved'); },
    onError: (e) => setError(e.response?.data?.name?.[0] || 'Update failed'),
  });

  const deleteMutation = useMutation((id) => tab.remove(id), {
    onSuccess: () => { invalidate(); setDeleteId(null); setSuccess('Deleted'); },
    onError: () => setError('Delete failed'),
  });

  const bulkDeleteMutation = useMutation(
    () => Promise.all(selected.map((id) => tab.remove(id))),
    {
      onSuccess: () => { invalidate(); setSelected([]); setSuccess(`Deleted ${selected.length} tag(s)`); },
      onError: () => setError('Bulk delete failed'),
    }
  );

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const mergeMutation = useMutation(
    () => {
      const idsToMerge = selected.filter((id) => String(id) !== String(canonicalId));
      return tab.merge(Number(canonicalId), idsToMerge);
    },
    {
      onSuccess: (res) => {
        invalidate();
        setMergeOpen(false);
        setSelected([]);
        setCanonicalId('');
        setSuccess(
          `Merged ${res.data.merged} tag(s) into canonical. ${res.data.pages_updated} page(s) updated.`
        );
      },
      onError: (e) => setError(e.response?.data?.error || 'Merge failed'),
    }
  );

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id));

  return (
    <Box>
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }} action={
          <Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>
        }>
          Failed to load tags: {queryError?.response?.data?.detail || queryError?.message || 'Request failed'}
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 1 }}>{success}</Alert>}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Show</InputLabel>
          <Select
            value={pageSize}
            label="Show"
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[20, 50, 100, 200].map((n) => (
              <MenuItem key={n} value={n}>{n} per page</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddingNew(true)}
        >
          Add New
        </Button>
        {selected.length >= 2 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<MergeIcon />}
            color="warning"
            onClick={() => {
              setCanonicalId(String(selected[0]));
              setMergeOpen(true);
            }}
          >
            Merge Selected ({selected.length})
          </Button>
        )}
        {selected.length >= 1 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => setBulkDeleteOpen(true)}
          >
            Delete Selected ({selected.length})
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < rows.length}
                  checked={rows.length > 0 && selected.length === rows.length}
                  onChange={toggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Pages</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Add new row */}
            {addingNew && (
              <TableRow sx={{ background: '#e8f5e9' }}>
                <TableCell padding="checkbox" />
                <TableCell>
                  <TextField
                    value={newName}
                    size="small"
                    autoFocus
                    placeholder="Tag name…"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createMutation.mutate(newName)}
                    fullWidth
                  />
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <Button size="small" onClick={() => createMutation.mutate(newName)} disabled={!newName.trim()}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => { setAddingNew(false); setNewName(''); }}>
                    Cancel
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                  No tags found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) =>
                editingId === row.id ? (
                  <EditableRow
                    key={row.id}
                    row={row}
                    onSave={(id, name) => updateMutation.mutate({ id, name })}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selected.includes(row.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.name}
                        {supportsScan && scanningIds.has(row.id) && (
                          <Tooltip title="Scanning pages for this tag…">
                            <CircularProgress size={14} thickness={5} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {supportsScan && scanningIds.has(row.id) ? (
                        <Tooltip title="Scanning…">
                          <CircularProgress size={18} thickness={5} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="View tagged pages">
                          <Chip
                            label={row.page_count}
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={() =>
                              navigate(
                                `/admin/tags/${tab.key}/${row.id}/pages`,
                                { state: { tagName: row.name } }
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View tagged pages">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            navigate(
                              `/admin/tags/${tab.key}/${row.id}/pages`,
                              { state: { tagName: row.name } }
                            )
                          }
                        >
                          <ViewPagesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {supportsScan && (
                        <Tooltip title={scanningIds.has(row.id) ? 'Scanning…' : tab.scanLabel}>
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              disabled={scanningIds.has(row.id)}
                              onClick={() => triggerScan(row.id)}
                            >
                              {scanningIds.has(row.id)
                                ? <CircularProgress size={16} thickness={5} />
                                : <ScanIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => setEditingId(row.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {totalCount} total
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, v) => setPage(v)}
          size="small"
          color="primary"
        />
      </Box>

      {/* Bulk Delete Confirm Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete {selected.length} Tag(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {selected.length} tag(s) and remove all their page associations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={bulkDeleteMutation.isLoading}
            onClick={() => { bulkDeleteMutation.mutate(); setBulkDeleteOpen(false); }}
          >
            {bulkDeleteMutation.isLoading ? <CircularProgress size={18} /> : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge Tags</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the <strong>canonical (keep)</strong> tag. All other selected tags will be deleted
            and their page associations transferred to the canonical tag.
          </DialogContentText>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Selected tags:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {selected.map((id) => {
              const row = rows.find((r) => r.id === id);
              return row ? (
                <Chip
                  key={id}
                  label={`${row.name} (${row.page_count} pages)`}
                  color={String(canonicalId) === String(id) ? 'primary' : 'default'}
                  onClick={() => setCanonicalId(String(id))}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              ) : null;
            })}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Click a tag above to set it as the canonical (keep) tag.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!canonicalId || mergeMutation.isLoading}
            onClick={() => mergeMutation.mutate()}
          >
            {mergeMutation.isLoading ? <CircularProgress size={18} /> : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the tag. All page references will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate(deleteId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const AdminTagsPage = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [authReady, setAuthReady] = useState(false);

  React.useEffect(() => {
    if (!authAPI.isAuthenticated() || !authAPI.isAdmin()) {
      navigate('/');
      return;
    }
    authAPI.fetchMe()
      .then(() => setAuthReady(true))
      .catch(() => navigate('/'));
  }, [navigate]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color={colors.royalBlue}>
          Tag Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage service types, keywords, beneficiaries, beneficiary organizations, and co-organizers.
          Use the merge feature to consolidate duplicate tags across all pages.
        </Typography>
      </Box>

      <Paper variant="outlined">
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
        <Box sx={{ p: 3 }}>
          <TagPanel tab={TABS[tabIndex]} authReady={authReady} />
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminTagsPage;
